import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { isDatadogRUMEnabled, isDatadogLogsEnabled } from '@/lib/datadog'

/**
 * Posts any error to Datadog RUM. Safe to call when RUM is not configured (no-op).
 * Use in: ErrorBoundary.onError, useErrorHandler, window.unhandledrejection.
 */
export function reportToDatadog(error: unknown, context?: Record<string, unknown>) {
	if (!isDatadogRUMEnabled()) return
	const err = error instanceof Error ? error : new Error(String(error))
	datadogRum.addError(err, context)
}

/**
 * Logs a recoverable warning to Datadog Logs (not RUM).
 * Use in catch blocks where the app continues after the failure.
 * All catch blocks must go through here — never import datadogLogs directly.
 */
export function logWarning(message: string, context?: Record<string, unknown>) {
	if (!isDatadogLogsEnabled()) return
	datadogLogs.logger.warn(message, context)
}

/**
 * Logs an error-level entry to Datadog Logs (not RUM).
 * Use for errors that need log-level tracking (e.g. QueryCache/MutationCache failures).
 * For RUM error tracking use reportToDatadog instead.
 */
export function logError(message: string, context?: Record<string, unknown>) {
	if (!isDatadogLogsEnabled()) return
	datadogLogs.logger.error(message, context)
}

/**
 * Logs an info-level entry to Datadog Logs. Use for diagnostic events (e.g. unexpected null state).
 * Non-throwing — synchronous SDK errors are suppressed so callers need no guard.
 */
export function logInfo(message: string, context?: Record<string, unknown>) {
	if (!isDatadogLogsEnabled()) return
	try {
		datadogLogs.logger.info(message, context)
	} catch {
		/* suppress — telemetry errors must never surface to users */
	}
}

/**
 * Tracks a custom RUM action for funnel analysis. Non-throwing — synchronous SDK errors are
 * suppressed at the service boundary so callers need no try/catch.
 */
export function trackAction(name: string, context?: Record<string, unknown>) {
	if (!isDatadogRUMEnabled()) return
	try {
		datadogRum.addAction(name, context)
	} catch {
		/* suppress — telemetry errors must never surface to users */
	}
}

/**
 * Set datadog RUM context properties.
 */
export function setRumContextProperty(key: string, value: unknown) {
	if (!isDatadogRUMEnabled()) return
	try {
		datadogRum.setGlobalContextProperty(key, value)
	} catch {
		/* suppress — telemetry errors must never surface to users */
	}
}
